import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';

// Opplane logo
import opplaneLogo from '../../assets/opplane-logo.png';

const PasswordChange = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { completeNewPasswordChallenge, passwordChallengeData } = useAuth();
  const navigate = useNavigate();
  
  // If there's no password challenge data, redirect to login
  if (!passwordChallengeData) {
    navigate('/login');
    return null;
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      setErrorMessage('Please enter and confirm your new password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }
    
    // Password validation
    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return;
    }
    
    // Check for uppercase, lowercase, number, and special character
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);
    
    if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
      setErrorMessage('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      
      const result = await completeNewPasswordChallenge(newPassword);
      
      if (result) {
        // Navigation will happen in the auth context
      }
    } catch (err) {
      console.error('Password change error:', err);
      setErrorMessage(err.response?.data?.message || 'Password change failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <PasswordChangeContainer>
      <PasswordChangeCard>
        <LogoContainer>
          <OpplaneLogo src={opplaneLogo} alt="Opplane Logo" />
        </LogoContainer>
        
        <PasswordChangeForm onSubmit={handleSubmit}>
          <FormTitle>Change Your Password</FormTitle>
          <FormSubtitle>
            Your account requires a password change. Please set a new password to continue.
          </FormSubtitle>
          
          <FormGroup>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (errorMessage) setErrorMessage('');
              }}
              placeholder="Enter new password"
              disabled={isSubmitting}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errorMessage) setErrorMessage('');
              }}
              placeholder="Confirm new password"
              disabled={isSubmitting}
              required
            />
          </FormGroup>
          
          <PasswordRequirements>
            <RequirementTitle>Password Requirements:</RequirementTitle>
            <RequirementList>
              <Requirement>At least 8 characters long</Requirement>
              <Requirement>At least one uppercase letter</Requirement>
              <Requirement>At least one lowercase letter</Requirement>
              <Requirement>At least one number</Requirement>
              <Requirement>At least one special character</Requirement>
            </RequirementList>
          </PasswordRequirements>
          
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Changing Password...' : 'Change Password'}
          </SubmitButton>
        </PasswordChangeForm>
      </PasswordChangeCard>
    </PasswordChangeContainer>
  );
};

// Styled components
const PasswordChangeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: ${props => props.theme.spacing.lg};
`;

const PasswordChangeCard = styled.div`
  background-color: ${props => props.theme.colors.verizonWhite};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.large};
  width: 100%;
  max-width: 450px;
  padding: ${props => props.theme.spacing.xl};
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const PasswordChangeForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FormTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.large};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xs};
  text-align: center;
`;

const FormSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: center;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: ${props => props.theme.fontSizes.medium};
  transition: ${props => props.theme.transitions.short};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.opplaneBlue};
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.background};
    cursor: not-allowed;
  }
`;

const PasswordRequirements = styled.div`
  background-color: rgba(0, 102, 204, 0.05);
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const RequirementTitle = styled.p`
  font-weight: bold;
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text};
`;

const RequirementList = styled.ul`
  margin: 0;
  padding-left: ${props => props.theme.spacing.lg};
`;

const Requirement = styled.li`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.textSecondary};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  background-color: rgba(0, 102, 204, 0.1);
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.opplaneBlue};
  color: ${props => props.theme.colors.verizonWhite};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.medium};
  font-weight: bold;
  cursor: pointer;
  transition: ${props => props.theme.transitions.short};
  margin-top: ${props => props.theme.spacing.md};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.opplaneDarkBlue};
  }
  
  &:disabled {
    background-color: rgba(0, 102, 204, 0.5);
    cursor: not-allowed;
  }
`;

const OpplaneLogo = styled.img`
  height: 20px;
`;

export default PasswordChange;
